import React, { useState, useEffect } from "react";
import {
  AcademicCapIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  Bars3Icon,
  XMarkIcon,
  FunnelIcon,
  ExclamationCircleIcon,
  PresentationChartLineIcon,
} from "@heroicons/react/24/outline";
import {
  useGetLessonViews,
  useGetTopicViews,
  useGetLessonCompletedCount,
} from "../../api/queries";
import { UserSegmentEnum } from "../../types";
import Button from "../../componentLibrary/Button";
import BarChartWidget from "./components/reusable/BarChartWidget";
import FeedbackAnalyticsWidget from "./components/FeedbackAnalyticsWidget";
import FeedbackDisplayWidget from "./components/FeedbackDisplayWidget";
import VideoAnalyticsWidget from "./components/VideoAnalyticsWidget";
import DeviceStatisticsWidget from "./components/DeviceStatisticsWidget";
import AnalyticsFilters from "./components/AnalyticsFilters";
import TopicViewHierarchical from "./components/TopicViewHierarchical";
import ErrorReportWidget from "./components/ErrorReportWidget";
import UsageOverTimeWidget from "./components/UsageOverTimeWidget";

// Valid tab types - single source of truth
const VALID_TABS = [
  "usageOverTime",
  "learning",
  "video",
  "devices",
  "feedback",
  "errors",
] as const;
type TabType = (typeof VALID_TABS)[number];

// Helper function to load selected segments from localStorage
const loadSelectedSegments = (): UserSegmentEnum[] => {
  try {
    const stored = localStorage.getItem("analyticsSelectedSegments");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter((value) =>
          Object.values(UserSegmentEnum).includes(value as UserSegmentEnum),
        );
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return [];
};

const loadStartDate = (): string | undefined => {
  try {
    const stored = localStorage.getItem("analyticsStartDate");
    return stored || undefined;
  } catch {
    // Ignore storage errors
  }
  return undefined;
};

const loadEndDate = (): string | undefined => {
  try {
    const stored = localStorage.getItem("analyticsEndDate");
    return stored || undefined;
  } catch {
    // Ignore storage errors
  }
  return undefined;
};

const loadActiveTab = (): TabType => {
  try {
    const stored = localStorage.getItem("analyticsActiveTab");
    if (stored && (VALID_TABS as readonly string[]).includes(stored)) {
      return stored as TabType;
    }
  } catch {
    // Ignore storage errors
  }
  return "usageOverTime";
};

const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabType>(loadActiveTab);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedSegments, setSelectedSegments] =
    useState<UserSegmentEnum[]>(loadSelectedSegments);
  const [startDate, setStartDate] = useState<string | undefined>(loadStartDate);
  const [endDate, setEndDate] = useState<string | undefined>(loadEndDate);
  const [selectedFeedbackLessonId, setSelectedFeedbackLessonId] =
    useState<string>("");
  const [selectedFeedbackTopicId, setSelectedFeedbackTopicId] =
    useState<string>("");
  const [isCustomTimeframe, setIsCustomTimeframe] = useState<boolean>(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        "analyticsSelectedSegments",
        JSON.stringify(selectedSegments),
      );
    } catch {
      // Ignore storage errors
    }
  }, [selectedSegments]);

  useEffect(() => {
    try {
      if (startDate) {
        localStorage.setItem("analyticsStartDate", startDate);
      } else {
        localStorage.removeItem("analyticsStartDate");
      }
    } catch {
      // Ignore storage errors
    }
  }, [startDate]);

  useEffect(() => {
    try {
      if (endDate) {
        localStorage.setItem("analyticsEndDate", endDate);
      } else {
        localStorage.removeItem("analyticsEndDate");
      }
    } catch {
      // Ignore storage errors
    }
  }, [endDate]);

  useEffect(() => {
    try {
      localStorage.setItem("analyticsActiveTab", activeTab);
    } catch {
      // Ignore storage errors
    }
  }, [activeTab]);

  const { data: lessonViews = [] } = useGetLessonViews(
    selectedSegments,
    startDate,
    endDate,
  );
  const { data: topicViews = [] } = useGetTopicViews(
    selectedSegments,
    startDate,
    endDate,
  );
  const { data: lessonCompletedCount = [] } = useGetLessonCompletedCount(
    selectedSegments,
    startDate,
    endDate,
  );

  const tabs = [
    {
      id: "usageOverTime" as TabType,
      label: "Usage Over Time",
      icon: <PresentationChartLineIcon className="w-5 h-5" />,
    },
    {
      id: "learning" as TabType,
      label: "Topic Views",
      icon: <AcademicCapIcon className="w-5 h-5" />,
    },
    {
      id: "video" as TabType,
      label: "Video Analytics",
      icon: <VideoCameraIcon className="w-5 h-5" />,
    },
    {
      id: "devices" as TabType,
      label: "Device Statistics",
      icon: <DevicePhoneMobileIcon className="w-5 h-5" />,
    },
    {
      id: "feedback" as TabType,
      label: "Feedback",
      icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
    },
    {
      id: "errors" as TabType,
      label: "Errors",
      icon: <ExclamationCircleIcon className="w-5 h-5" />,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "usageOverTime":
        return (
          <UsageOverTimeWidget
            selectedSegments={selectedSegments}
            startDate={startDate}
            endDate={endDate}
          />
        );

      case "learning":
        return (
          <div className="grid gap-4">
            <div className="grid grid-cols-1 gap-4">
              <TopicViewHierarchical
                data={topicViews}
                title="Topics"
                selectedSegments={selectedSegments}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BarChartWidget
                data={lessonViews}
                title="Lessons"
                metric="views"
              />
              <BarChartWidget
                data={lessonCompletedCount}
                title="Lessons"
                metric="completions"
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <BarChartWidget data={topicViews} title="Topics" metric="views" />
            </div>
          </div>
        );

      case "video":
        return (
          <VideoAnalyticsWidget
            selectedSegments={selectedSegments}
            startDate={startDate}
            endDate={endDate}
          />
        );

      case "devices":
        return (
          <DeviceStatisticsWidget
            selectedSegments={selectedSegments}
            startDate={startDate}
            endDate={endDate}
          />
        );

      case "feedback":
        return (
          <div className="grid gap-4">
            <FeedbackAnalyticsWidget
              selectedSegments={selectedSegments}
              startDate={startDate}
              endDate={endDate}
              onLessonSelect={(lessonId, topicId) => {
                setSelectedFeedbackLessonId(lessonId);
                setSelectedFeedbackTopicId(topicId);
              }}
            />
            <FeedbackDisplayWidget
              selectedSegments={selectedSegments}
              startDate={startDate}
              endDate={endDate}
              externalLessonId={selectedFeedbackLessonId}
              externalTopicId={selectedFeedbackTopicId}
              onClearExternalSelection={() => {
                setSelectedFeedbackLessonId("");
                setSelectedFeedbackTopicId("");
              }}
            />
          </div>
        );

      case "errors":
        return (
          <ErrorReportWidget
            selectedSegments={selectedSegments}
            startDate={startDate}
            endDate={endDate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      <AnalyticsFilters
        selectedSegments={selectedSegments}
        onSegmentsChange={setSelectedSegments}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        mobileMenuButton={
          <Button
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              if (!isMobileMenuOpen) {
                setIsMobileFilterOpen(false);
              }
            }}
            variant="secondary"
            size="md"
            className="flex items-center gap-2 whitespace-nowrap w-full justify-start"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Bars3Icon className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="truncate">
              {tabs.find((tab) => tab.id === activeTab)?.label}
            </span>
          </Button>
        }
        mobileFilterButton={
          <Button
            onClick={() => {
              setIsMobileFilterOpen(!isMobileFilterOpen);
              if (!isMobileFilterOpen) {
                setIsMobileMenuOpen(false);
              }
            }}
            variant="secondary"
            size="md"
            className="flex items-center gap-2 whitespace-nowrap w-full justify-start"
          >
            {isMobileFilterOpen ? (
              <XMarkIcon className="w-5 h-5 flex-shrink-0" />
            ) : (
              <FunnelIcon className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="truncate">Filters</span>
          </Button>
        }
        onCustomTimeframeChange={setIsCustomTimeframe}
      />

      <div
        className={`p-4 pt-24 ${isCustomTimeframe ? "lg:pt-52" : "lg:pt-40"}`}
      >
        {/* Tab Navigation - Fixed (Desktop Only) */}
        <div
          className={`hidden lg:block fixed left-0 right-0 z-40 bg-gray-50 px-4 py-2 ${isCustomTimeframe ? "top-40" : "top-28"}`}
        >
          <div className="bg-white rounded-lg shadow-md p-2">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  variant={activeTab === tab.id ? "dark" : "secondary"}
                  size="md"
                  className={`
                  flex items-center gap-2 transition-all duration-200
                  ${activeTab === tab.id ? "shadow-lg transform scale-105" : ""}
                `}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden fixed left-0 right-0 z-[45] bg-white shadow-lg"
            style={{ top: "calc(3rem + 88px)" }}
          >
            <div className="px-4 py-2 space-y-2 border-t border-gray-200">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  variant={activeTab === tab.id ? "dark" : "secondary"}
                  size="md"
                  className={`
                  w-full flex items-center gap-2 transition-all duration-200
                  ${activeTab === tab.id ? "shadow-lg" : ""}
                `}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Dropdown Filter */}
        {isMobileFilterOpen && (
          <div
            className="lg:hidden fixed left-0 right-0 z-[45] bg-white shadow-lg max-h-[70vh] overflow-y-auto"
            style={{ top: "calc(3rem + 88px)" }}
          >
            <div className="px-4 py-4 border-t border-gray-200">
              <AnalyticsFilters
                selectedSegments={selectedSegments}
                onSegmentsChange={setSelectedSegments}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                renderContentOnly={true}
              />
            </div>
          </div>
        )}

        <div className="animate-fadeIn">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
